import React, { useEffect, useState } from "react";
import { useMutation } from "react-relay";
import { KnowledgeResourceDynamicFields } from "src/components/shared/KnowledgeResourceDynamicFields";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { useToast } from "src/hooks/use-toast";
import { useProductBranding } from "src/hooks/useProductBranding";

import type {
  CreateOnboardingRequestMutation as CreateOnboardingRequestMutationType,
  OnboardingType,
} from "../mutations/__generated__/CreateOnboardingRequestMutation.graphql";
import { CreateOnboardingRequestMutation } from "../mutations/CreateOnboardingRequestMutation";


interface AddKnowledgeResourceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSubmit?: () => Promise<void> | void; // Optional callback to run when form is submitted
  onPreSuccess?: () => Promise<void> | void; // Optional callback to run before showing success toast
  onSkip?: () => Promise<void> | void; // Optional callback to run when Skip is pressed (e.g., to mark task as completed)
  integrations?: Array<{
    id: string;
    name: string;
    type: string;
    status: "connected" | "disconnected";
  }>;
  defaultResourceType?: OnboardingType; // Optional default resource type to pre-select
}

export interface KnowledgeResourceData {
  resourceType: OnboardingType;
  name?: string;
  description?: string;
  baseQueryFilter?: string;
  dashboardIds?: string;
  dashboardUrls?: string;
}

interface ResourceType {
  value: OnboardingType;
  label: string;
}

const resourceTypes: ResourceType[] = [
  { value: "DATADOG_DASHBOARD" as OnboardingType, label: "Datadog Dashboard" },
  { value: "DATADOG_NOTEBOOK" as OnboardingType, label: "Datadog Notebook" },
  { value: "NEW_RELIC_DASHBOARD" as OnboardingType, label: "New Relic Dashboard" },
  { value: "GRAFANA_CLOUD_DASHBOARD" as OnboardingType, label: "Grafana Dashboard" },
  { value: "GCP_DASHBOARD" as OnboardingType, label: "GCP Cloud Monitoring Dashboard" },
  { value: "NOTION_LINK" as OnboardingType, label: "Notion Page or Database" },
  { value: "CONFLUENCE_LINK" as OnboardingType, label: "Confluence Page" }
];

export default function AddKnowledgeResourceModal({
  open,
  onClose,
  onSuccess,
  onSubmit,
  onPreSuccess,
  onSkip,
  integrations = [],
  defaultResourceType,
}: AddKnowledgeResourceModalProps) {
  const { toast } = useToast();
  const { productName } = useProductBranding();
  const [createOnboardingRequest, isOnboardingInFlight] = useMutation<CreateOnboardingRequestMutationType>(CreateOnboardingRequestMutation);
  const [resourceType, setResourceType] = useState<OnboardingType | "">("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [baseQueryFilter, setBaseQueryFilter] = useState("");
  const [dashboardIds, setDashboardIds] = useState("");
  const [urls, setUrls] = useState("");
  const [notionLinks, setNotionLinks] = useState("");
  const [confluenceLinks, setConfluenceLinks] = useState("");
  const [selectedIntegrationId, setSelectedIntegrationId] = useState("");

  const isInFlight = isOnboardingInFlight;

  // Filter resource types based on available connected integrations
  const availableResourceTypes = resourceTypes.filter(resourceType => {
    if (resourceType.value === "DATADOG_DASHBOARD") {
      return integrations.some(integration =>
        integration.type === "DATADOG" && integration.status === "connected"
      );
    }
    if (resourceType.value === "DATADOG_NOTEBOOK") {
      return integrations.some(integration =>
        integration.type === "DATADOG" && integration.status === "connected"
      );
    }
    if (resourceType.value === "NEW_RELIC_DASHBOARD") {
      return integrations.some(integration =>
        integration.type === "NEW_RELIC" && integration.status === "connected"
      );
    }
    if (resourceType.value === "GRAFANA_CLOUD_DASHBOARD") {
      return integrations.some(integration =>
        integration.type === "GRAFANA_CLOUD" && integration.status === "connected"
      );
    }
    if (resourceType.value === "GCP_DASHBOARD") {
      return integrations.some(integration =>
        integration.type === "GCP_CLOUD_MONITORING" && integration.status === "connected"
      );
    }
    if (resourceType.value === "NOTION_LINK") {
      return integrations.some(integration =>
        integration.type === "NOTION" && integration.status === "connected"
      );
    }
    if (resourceType.value === "CONFLUENCE_LINK") {
      return integrations.some(integration =>
        integration.type === "CONFLUENCE" && integration.status === "connected"
      );
    }
    return true; // Keep other resource types
  });

  // Auto-select the data source if there's a default or only one available
  useEffect(() => {
    if (open && !resourceType) {
      if (defaultResourceType) {
        setResourceType(defaultResourceType);
      } else if (availableResourceTypes.length === 1) {
        setResourceType(availableResourceTypes[0].value);
      }
    }
  }, [open, defaultResourceType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get integrations for the selected resource type
  const getIntegrationsForResourceType = () => {
    if (!resourceType) return [];

    const typeMap: Record<string, string> = {
      "DATADOG_DASHBOARD": "DATADOG",
      "DATADOG_NOTEBOOK": "DATADOG",
      "NEW_RELIC_DASHBOARD": "NEW_RELIC",
      "GRAFANA_CLOUD_DASHBOARD": "GRAFANA_CLOUD",
      "GCP_DASHBOARD": "GCP_CLOUD_MONITORING",
    };

    const integrationType = typeMap[resourceType];
    if (!integrationType) return [];

    return integrations.filter(
      integration => integration.type === integrationType && integration.status === "connected"
    );
  };

  const availableIntegrations = getIntegrationsForResourceType();

  // Auto-select integration if there's only one
  useEffect(() => {
    if (open && resourceType && availableIntegrations.length === 1) {
      setSelectedIntegrationId(availableIntegrations[0].id);
    } else if (open && resourceType && availableIntegrations.length === 0) {
      setSelectedIntegrationId("");
    }
  }, [open, resourceType, availableIntegrations.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResourceTypeChange = (value: string) => {
    setResourceType(value as OnboardingType | "");
    // Clear all form fields when switching resource type
    setName("");
    setDescription("");
    setBaseQueryFilter("");
    setDashboardIds("");
    setUrls("");
    setNotionLinks("");
    setSelectedIntegrationId("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resourceType) {
      return; // Don't submit if no resource type selected
    }

    // Call onSubmit callback if provided (e.g., to mark task as PENDING)
    if (onSubmit) {
      try {
        await Promise.resolve(onSubmit());
      } catch (error) {
        console.error("onSubmit callback failed:", error);
        // Continue with submission even if onSubmit fails
      }
    }

    createOnboardingRequest({
      variables: {
        input: {
          resourceType: resourceType as OnboardingType,
          name: name || undefined,
          description: description || undefined,
          baseQueryFilter: baseQueryFilter || undefined,
          dashboardIds: dashboardIds || undefined,
          dashboardUrls: urls || undefined,
          notionLinks: notionLinks || undefined,
          confluenceLinks: confluenceLinks || undefined,
          integrationId: selectedIntegrationId || undefined,
        }
      },
      onCompleted: (response) => {
        if (response.createOnboarding.error) {
          toast({
            variant: "error",
            title: "Failed to add resource",
            description: response.createOnboarding.error,
          });
        } else {
          const showSuccess = () => {
            toast({
              variant: "success",
              title: "Resource added",
              description: "This resource has been queued for processing.",
            });
            handleClose();
            onSuccess();
          };

          if (onPreSuccess) {
            Promise.resolve(onPreSuccess())
              .then(showSuccess)
              .catch((error) => {
                console.warn("onPreSuccess failed:", error);
                showSuccess();
              });
          } else {
            showSuccess();
          }
        }
      },
      onError: (error) => {
        toast({
          variant: "error",
          title: "Failed to add resource",
          description: error.message || "An unexpected error occurred.",
        });
      },
    });
  };

  const handleClose = () => {
    setResourceType("");
    setName("");
    setDescription("");
    setBaseQueryFilter("");
    setDashboardIds("");
    setUrls("");
    setNotionLinks("");
    setSelectedIntegrationId("");
    onClose();
  };

  const handleSkip = async () => {
    // Call onSkip callback if provided (e.g., to mark task as completed)
    if (onSkip) {
      try {
        await Promise.resolve(onSkip());
      } catch (error) {
        console.error("onSkip callback failed:", error);
      }
    }
    handleClose();
  };


  const isFormValid = () => {
    if (!resourceType) return false;

    // For dashboard types, require integration selection if there are multiple integrations
    if (["DATADOG_DASHBOARD", "DATADOG_NOTEBOOK", "NEW_RELIC_DASHBOARD", "GRAFANA_CLOUD_DASHBOARD", "GCP_DASHBOARD"].includes(resourceType)) {
      if (availableIntegrations.length > 1 && !selectedIntegrationId) {
        return false;
      }
    }

    switch (resourceType) {
      case "DATADOG_LOG_TYPE":
      case "DATADOG_EVENT_TYPE":
        return name.trim() !== "" && description.trim() !== "" && baseQueryFilter.trim() !== "";
      case "DATADOG_DASHBOARD":
      case "DATADOG_NOTEBOOK":
      case "NEW_RELIC_DASHBOARD":
      case "GRAFANA_CLOUD_DASHBOARD":
      case "GCP_DASHBOARD":
        return dashboardIds.trim() !== "" || urls.trim() !== "";
      case "NOTION_LINK":
        return notionLinks.trim() !== "";
      case "CONFLUENCE_LINK":
        return confluenceLinks.trim() !== "";
      default:
        return false;
    }
  };

  const getDialogTitle = () => {
    return "Add Resource";
  };

  const getDialogDescription = () => {
    if (onSubmit) {
      return "Add at least one resource or press Skip to continue. You can add more resources later.";
    }
    return `Add a resource that ${productName} should know about and use when handling investigations and requests.`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent variant="large" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="space-y-4 overflow-y-auto flex-1 px-1 pb-2">
            {!defaultResourceType && (
              <div>
                <Label htmlFor="resourceType">
                  Resource Type
                </Label>
                <Select value={resourceType} onValueChange={handleResourceTypeChange}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a resource type" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableResourceTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show integration selector if there are multiple integrations for the selected resource type */}
            {resourceType && availableIntegrations.length > 1 && (
              <div>
                <Label htmlFor="integration">
                  Integration
                </Label>
                <Select value={selectedIntegrationId} onValueChange={setSelectedIntegrationId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select an integration" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableIntegrations.map((integration) => (
                      <SelectItem key={integration.id} value={integration.id}>
                        {integration.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {resourceType && (
              <KnowledgeResourceDynamicFields
                resourceType={resourceType}
                values={{
                  name,
                  description,
                  baseQueryFilter,
                  dashboardIds,
                  urls,
                  notionLinks,
                  confluenceLinks,
                }}
                onChange={{
                  onNameChange: setName,
                  onDescriptionChange: setDescription,
                  onBaseQueryFilterChange: setBaseQueryFilter,
                  onDashboardIdsChange: setDashboardIds,
                  onUrlsChange: setUrls,
                  onNotionLinksChange: setNotionLinks,
                  onConfluenceLinksChange: setConfluenceLinks,
                }}
                className="bg-card border-border"
                productName={productName}
              />
            )}
          </div>

          <DialogFooter className="mt-6 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onSubmit ? handleSkip : handleClose}
            >
              {onSubmit ? "Skip" : "Cancel"}
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid() || isInFlight}
              variant={isFormValid() ? "default" : "outline"}
            >
              {isInFlight
                ? "Adding..."
                : "Add"
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}